import {
  ApplicationRef,
  createNgModule,
  inject,
  Injectable,
  Type
} from "@angular/core";
import {
  catchError,
  defer,
  forkJoin,
  iif,
  lastValueFrom,
  map,
  of,
  switchMap
} from "rxjs";
import { InstallationBackend } from "src/app/backend/api/installation.backend";
import { IntegrationBackend } from "src/app/backend/api/integration.backend";
import {
  Installation,
  InstallationInstalled,
  InstallationStatus
} from "src/app/backend/models/installation";
import { Integration } from "src/app/backend/models/integration";
import { isPromise } from "src/app/support/promise";
import {
  IntegrationModule,
  PreInstallResult
} from "src/app/integrations/integration-module";
import {
  IntegrationDropPoints,
  IntegrationLayout,
  IntegrationLayoutAction,
  IntegrationLayoutDataGridColumn,
  IntegrationLayoutFeature,
  IntegrationLayoutMenu,
  IntegrationLayoutProperty,
  IntegrationLayoutTab,
  IntegrationLayoutWarning
} from "src/app/integrations/integration-layout";
import { UserService } from "src/app/services/user.service";
import { flat } from "radash";
import { sntLog } from "src/app/common/logger";

const logger = sntLog("IntegrationService");
@Injectable()
export class IntegrationService {
  private readonly installationBackend = inject(InstallationBackend);
  private readonly integrationBackend = inject(IntegrationBackend);

  private readonly userService = inject(UserService);
  private readonly applicationRef = inject(ApplicationRef);

  private installationsEnabled: InstallationInstalled[] = [];
  private integrations: Integration[] = [];

  private readonly integrationsLayoutStore: Record<string, IntegrationLayout> =
    {};

  // TODO spostare questa mappa in integration.module
  map: Record<string, () => Promise<Type<IntegrationModule>>> = {
    ["snoots-mcp"]: () =>
      import("../integrations/integrations-modules/snoots-mcp/snoots-mcp.module").then(
        (m) => m.SnootsMCPModule
      ),
    ["vetinfo-scorta"]: () =>
      import("../integrations/integrations-modules/vetinfo/vetinfo.module").then(
        (m) => m.VetinfoModule
      ),
    ["vetinfo-rev"]: () =>
      import("../integrations/integrations-modules/vetinfo-rev/vetinfo-rev.module").then(
        (m) => m.VetinfoRevModule
      ),
    ["idexx-vcplus"]: () =>
      import("../integrations/integrations-modules/idexx-vcplus/idexx-vcplus.module").then(
        (m) => m.IdexxVcplusModule
      ),
    ["brave-contabile"]: () =>
      import("../integrations/integrations-modules/brave-contabile/brave-contabile.module").then(
        (m) => m.BraveContabileModule
      ),
    ["is-abv"]: () =>
      import("../integrations/integrations-modules/is-abv/is-abv.module").then(
        (m) => m.IsAbvModule
      )
  };

  async init(): Promise<void> {
    await this.loadAll();
  }

  private async loadAll(): Promise<void> {
    const { installations, integrations } = await lastValueFrom(
      forkJoin({
        installations: iif(
          () => this.userService.able("read", "installation"),
          defer(() => {
            return this.installationBackend.listAll().pipe(
              switchMap((installations: Installation[]) => {
                return iif(
                  () => installations?.length > 0,
                  defer(() => {
                    return forkJoin(
                      installations.map((installation: Installation) =>
                        this.installationBackend.loadById(installation.id)
                      )
                    );
                  }),
                  defer(() => of([]))
                );
              }),
              catchError((error) => {
                logger.error("Error loading installations", error);
                return of([]);
              })
            );
          }),
          defer(() => {
            return of([]);
          })
        ),
        integrations: iif(
          () => this.userService.able("read", "integration"),
          defer(() => {
            return this.integrationBackend.listAll().pipe(
              catchError((error) => {
                logger.error("Error loading integrations", error);
                return of([]);
              })
            );
          }),
          defer(() => {
            return of([]);
          })
        )
      }).pipe(
        map(
          ({
            installations,
            integrations
          }: {
            installations: Installation[];
            integrations: Integration[];
          }) => {
            return {
              installations: installations.filter(
                (installation: Installation) =>
                  installation.status !== InstallationStatus.DISABLED
              ),
              integrations
            };
          }
        ),
        map(
          ({
            installations,
            integrations
          }: {
            installations: Installation[];
            integrations: Integration[];
          }) => {
            const installationsWithIntegration = installations.map(
              (installation: Installation) => {
                const integration = integrations.find(
                  (x) => x.id === installation.integrationId
                );

                return <InstallationInstalled>{
                  ...installation,
                  integration
                };
              }
            );

            return {
              installations: installationsWithIntegration,
              integrations
            };
          }
        ),
        map(
          ({
            installations,
            integrations
          }: {
            installations: InstallationInstalled[];
            integrations: Integration[];
          }) => {
            return {
              installations: installations.filter(
                (installation: InstallationInstalled) => {
                  return installation.status !== InstallationStatus.DISABLED;
                }
              ),
              integrations
            };
          }
        )
      )
    );

    this.installationsEnabled = installations;
    this.integrations = integrations;

    for (const installation of installations) {
      await this.installIntegration(installation.integration);
    }
  }

  getById(id: string): Integration {
    return this.integrations.find((x) => x.id === id)!;
  }

  getInstallationByIntegrationId(
    integrationId: string
  ): Installation | undefined {
    return this.installationsEnabled.find(
      (x) => x.integrationId === integrationId
    );
  }

  getInstallationById(installationId: string): InstallationInstalled {
    return this.installationsEnabled.find((x) => x.id === installationId)!;
  }

  private async installIntegration(integration: Integration): Promise<void> {
    if (this.map[integration.id]) {
      const module: Type<IntegrationModule> = await this.map[integration.id]();
      const moduleRef = createNgModule(module, this.applicationRef.injector);

      this.integrationsLayoutStore[integration.id] =
        moduleRef.instance.getLayout();
    }
  }

  getMenusByDropPoint(
    integrationDropPoint: IntegrationDropPoints
  ): IntegrationLayoutMenu[] {
    return this.#getIntegrationLayoutFeature<IntegrationLayoutMenu>({
      dropPoint: integrationDropPoint,
      extractor: (layout: IntegrationLayout | undefined) => {
        return layout?.menu || [];
      }
    });
  }

  getTabsByIntegrationId(integrationId: string): IntegrationLayoutTab[] {
    return this.#getIntegrationLayoutFeatureByIntegrationId({
      integrationId
    });
  }

  getActionsByDropPoint(
    integrationDropPoint: IntegrationDropPoints
  ): IntegrationLayoutAction[] {
    return this.#getIntegrationLayoutFeature<IntegrationLayoutAction>({
      dropPoint: integrationDropPoint,
      extractor: (layout: IntegrationLayout | undefined) => {
        return layout?.actions || [];
      }
    });
  }

  getDataGridColumnsByDropPoint(
    integrationDropPoint: IntegrationDropPoints
  ): IntegrationLayoutDataGridColumn[] {
    return this.#getIntegrationLayoutFeature<IntegrationLayoutDataGridColumn>({
      dropPoint: integrationDropPoint,
      extractor: (layout: IntegrationLayout | undefined) => {
        return layout?.dataGridColumns || [];
      }
    });
  }

  getButtonsByDropPoint(
    integrationDropPoint: IntegrationDropPoints
  ): IntegrationLayoutAction[] {
    return this.#getIntegrationLayoutFeature<IntegrationLayoutAction>({
      dropPoint: integrationDropPoint,
      extractor: (layout: IntegrationLayout | undefined) => {
        return layout?.buttons || [];
      }
    });
  }

  getWarningsByDropPoint(
    integrationDropPoint: IntegrationDropPoints
  ): IntegrationLayoutWarning[] {
    return this.#getIntegrationLayoutFeature<IntegrationLayoutWarning>({
      dropPoint: integrationDropPoint,
      extractor: (layout: IntegrationLayout | undefined) => {
        return layout?.warnings || [];
      }
    });
  }

  getPropertiesByDropPoint(
    integrationDropPoint: IntegrationDropPoints
  ): IntegrationLayoutProperty[] {
    return this.#getIntegrationLayoutFeature<IntegrationLayoutProperty>({
      dropPoint: integrationDropPoint,
      extractor: (layout: IntegrationLayout | undefined) => {
        return layout?.properties || [];
      }
    });
  }

  #getIntegrationLayoutFeatureByIntegrationId<
    T extends IntegrationLayoutFeature = IntegrationLayoutFeature
  >({ integrationId }: { integrationId: string }): IntegrationLayoutTab[] {
    return this.integrationsLayoutStore[integrationId]?.tabs || [];
  }

  #getIntegrationLayoutFeature<
    T extends IntegrationLayoutFeature = IntegrationLayoutFeature
  >({
    dropPoint,
    extractor
  }: {
    dropPoint: IntegrationDropPoints;
    extractor: (layout: IntegrationLayout | undefined) => T[];
  }): T[] {
    return flat(
      this.installationsEnabled.map((installation: InstallationInstalled) => {
        const features = extractor(
          this.integrationsLayoutStore[installation.integrationId]
        );

        if (!features) {
          return [];
        }

        return features.filter((action: T) => {
          if (action.dropPoints.includes(dropPoint)) {
            action.installation = installation;
            return true;
          }

          return false;
        });
      })
    );
  }

  async executePreInstall({
    integration
  }: {
    integration: Integration;
  }): Promise<PreInstallResult> {
    const module: Type<IntegrationModule> = await this.map[integration.id]();
    const moduleRef = createNgModule(module, this.applicationRef.injector);

    if (!moduleRef.instance.preInstall) {
      return {
        success: true
      };
    }

    return await moduleRef.instance.preInstall();
  }

  async executeIntegrationAction({
    installation,
    action,
    params
  }: {
    installation: Installation;
    action: IntegrationLayoutAction;
    params?: Record<string, unknown>;
  }): Promise<void> {
    const fnPromise: any = action.do({ installation, ...(params || {}) });

    if (isPromise(fnPromise)) {
      await fnPromise;
    }
  }

  getInstallationProperty({
    installationId,
    propertyName
  }: {
    installationId: string;
    propertyName: string;
  }): string | number | boolean | null {
    const installation = this.installationsEnabled.find(
      (x) => x.id === installationId
    );

    if (!installation || !installation.configuration) {
      return null;
    }

    return installation.configuration[propertyName];
  }

  async updateInstallationProperty({
    installationId,
    propertyName,
    propertyValue
  }: {
    installationId: string;
    propertyName: string;
    propertyValue: string | number | boolean;
  }): Promise<void> {
    await lastValueFrom(
      this.installationBackend.updateProperty({
        id: installationId,
        propertyName,
        propertyValue
      })
    );

    const installation = this.installationsEnabled.find((x) => x.id);
    if (installation) {
      if (!installation.configuration) {
        installation.configuration = {};
      }

      installation.configuration[propertyName] = propertyValue;
    }
  }
}
